import { CalendarRange, ChevronRight, FileBarChart, Search, Tag, X } from "lucide-react";
import { useState } from "react";
import type { SocialReportSummary } from "@/shared/api/api-client";
import { AppLink } from "@/shared/components/AppLink";
import { Pager } from "@/shared/components/Pager";
import { ToolbarSelect } from "@/shared/components/ToolbarSelect";
import { currentMonthRange, formatWindowFromCriteria, previousMonthRange, rangeDates } from "@/shared/utils/date-range";
import { foldText } from "@/shared/utils/text-search";

import { formatCena, formatInteger, formatShortDate } from "@/shared/utils/format";

/** O recorte da lista de salvos: marca e período vão pro servidor
 *  (`GET /social-reports?brand_id&date_from&date_to`, que já existiam);
 *  a busca por nome é local, porque o servidor não busca por nome. */
export type SavedReportsQuery = {
  brandId: number | null;
  period: SavedPeriod;
  page: number;
};

export type SavedPeriod = "all" | "current" | "previous" | "90d" | "year";

export const SAVED_PAGE_SIZE = 20;

/** Período → janela `date_from`/`date_to`. O servidor devolve os
 *  relatórios cuja janela CRUZA esse período. */
export function savedPeriodRange(period: SavedPeriod): { from?: string; to?: string } {
  if (period === "current") return currentMonthRange();
  if (period === "previous") return previousMonthRange();
  if (period === "90d") return rangeDates("90d");
  if (period === "year") return rangeDates("year");
  return {};
}

const PERIOD_OPTIONS: { value: SavedPeriod; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "current", label: "Este mês" },
  { value: "previous", label: "Mês passado" },
  { value: "90d", label: "90 dias" },
  { value: "year", label: "Este ano" }
];

/**
 * Os relatórios já salvos, na própria tela de relatórios.
 *
 * São todos os da EMPRESA, nunca só os de quem abriu a tela: o
 * relatório é do time que atende o cliente, e `saved_by` aparece como
 * informação, não como recorte.
 *
 * Desde 2026-10-13 é uma tabela paginada com filtro de marca e período
 * (antes eram os 20 mais recentes e o resto sumia daqui).
 */
export function SavedReportsList({
  reports,
  total,
  query,
  onQuery,
  brands,
  activeReportId,
  reportHref,
  onOpen
}: {
  reports: SocialReportSummary[];
  total: number;
  query: SavedReportsQuery;
  onQuery: (next: SavedReportsQuery) => void;
  brands: { id: number; name: string }[];
  activeReportId: number | null;
  /** Para onde o relatório abre numa aba nova (2026-10-26). A linha é um
   *  link de verdade: o clique simples continua abrindo AQUI, na própria
   *  página (`onOpen`), e Ctrl/Meta+clique, clique do meio e "abrir em nova
   *  aba" abrem este endereço, que a página lê ao montar. */
  reportHref: (report: SocialReportSummary) => string;
  onOpen: (report: SocialReportSummary) => void;
}) {
  const [search, setSearch] = useState("");
  const filtered = query.brandId !== null || query.period !== "all";
  if (total === 0 && !filtered) return null;

  const term = foldText(search.trim());
  const nameOf = (report: SocialReportSummary) =>
    report.name ?? formatWindowFromCriteria(report.generation_criteria, report.window_start, report.window_end);
  const visible = term ? reports.filter(report => foldText(nameOf(report)).includes(term)) : reports;

  return (
    <section className="card saved-reports">
      <header className="saved-reports-top">
        <p className="saved-reports-head">
          <FileBarChart size={16}/> Amostras salvas
          <span className="saved-reports-total">{formatInteger(total)}</span>
        </p>
        <div className="saved-reports-filters">
          <label className="saved-reports-search">
            <Search size={15} aria-hidden="true"/>
            <input
              type="search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Buscar nesta página…"
              aria-label="Buscar amostras salvas pelo nome, nesta página"
            />
            {search && (
              <button type="button" aria-label="Limpar busca" onClick={() => setSearch("")}>
                <X size={13}/>
              </button>
            )}
          </label>
          {brands.length > 0 && (
            <ToolbarSelect<string>
              prefix="Marca"
              icon={Tag}
              ariaLabel="Filtrar amostras salvas por marca"
              value={query.brandId === null ? "all" : String(query.brandId)}
              onChange={value => onQuery({ ...query, brandId: value === "all" ? null : Number(value), page: 1 })}
              options={[
                { value: "all", label: "Todas" },
                ...brands.map(brand => ({ value: String(brand.id), label: brand.name }))
              ]}
            />
          )}
          <ToolbarSelect<SavedPeriod>
            prefix="Período"
            icon={CalendarRange}
            ariaLabel="Filtrar amostras salvas por período"
            align="right"
            value={query.period}
            onChange={period => onQuery({ ...query, period, page: 1 })}
            options={PERIOD_OPTIONS}
          />
        </div>
      </header>

      {visible.length === 0 ? (
        <p className="saved-reports-empty">
          {term
            ? "Nenhuma amostra desta página tem esse nome."
            : "Nenhuma amostra salva neste filtro."}
        </p>
      ) : (
        // Sem os papéis de tabela (2026-10-26): uma linha que é link não
        // pode ser `role="row"` — o papel trocaria o de link, e a linha
        // deixaria de ser navegável pelo leitor de tela. O cabeçalho é só
        // visual, e cada linha lê o conteúdo em ordem.
        <div className="saved-reports-table">
          <div className="saved-reports-row is-head" aria-hidden="true">
            <span>Nome</span>
            <span>Período</span>
            <span className="is-num">Publicações</span>
            <span className="is-num">ICena médio</span>
            <span>Salva em</span>
            <span/>
          </div>
          {visible.map(report => {
            const active = report.id === activeReportId;
            const cells = (
              <>
                <span className="saved-reports-name">
                  <strong>{nameOf(report)}</strong>
                  {report.brand_name && <small>{report.brand_name}</small>}
                </span>
                <span className="saved-reports-period">
                  {formatWindowFromCriteria(report.generation_criteria, report.window_start, report.window_end)}
                </span>
                <span className="is-num">{formatInteger(report.aggregates.post_count)}</span>
                <span className="is-num is-strong">
                  {report.aggregates.avg_icena != null ? formatCena(report.aggregates.avg_icena) : "—"}
                </span>
                <span className="saved-reports-date">
                  {formatShortDate(new Date(report.saved_at))}
                </span>
                <span className="saved-reports-go" aria-hidden="true">
                  {active ? "Aberto" : <ChevronRight size={16}/>}
                </span>
              </>
            );
            // O relatório que já está aberto não leva a lugar nenhum.
            if (active) {
              return (
                <div key={report.id} className="saved-reports-row is-active" aria-current="true" title="Aberto">
                  {cells}
                </div>
              );
            }
            return (
              <AppLink
                key={report.id}
                to={reportHref(report)}
                className="saved-reports-row"
                onPlainClick={() => onOpen(report)}
                title={`Abrir ${nameOf(report)}`}
              >
                {cells}
              </AppLink>
            );
          })}
        </div>
      )}

      <Pager
        page={query.page}
        size={SAVED_PAGE_SIZE}
        count={{ total }}
        onPage={page => onQuery({ ...query, page })}
      />
    </section>
  );
}
